import { astro } from 'iztro';
import { Solar } from 'lunar-typescript';
import { validateBirthDateTime } from './dateValidation';
import { calculateTrueSolarTime, inputTimeToIndex } from './time';

export type EnglishMvpGender = 'male' | 'female';

export type EnglishMvpSnapshot = {
  birthDate: string;
  effectiveBirthDate: string;
  effectiveBirthTime: string;
  timeAccuracy: {
    basis: 'civil' | 'true_solar';
    timeIndex: number;
    timeWindow: string;
    correctionMinutes: number | null;
    chartBoundaryChanged: boolean;
  };
  lunarDate: string;
  lunarDateNumeric: string;
  lunarLeapMonth: boolean;
  soul: string;
  body: string;
  life: { stars: string[]; range: string };
  wealth: { stars: string[]; range: string };
  career: { stars: string[]; range: string };
  palaces: Array<{
    key: string;
    label: string;
    stars: string[];
    supportingStars: string[];
    range: string;
    index: number;
    heavenlyStem: string;
    earthlyBranch: string;
    isBodyPalace: boolean;
    ages: number[];
    transformations: Array<{
      star: string;
      code: 'A' | 'B' | 'C' | 'D';
      label: string;
      traditionalName: string;
    }>;
  }>;
  currentCycle: { palace: string; range: string };
  nextCycle: { palace: string; range: string };
  annual: {
    year: number;
    nominalAge: number;
    palaceKey: string;
    palace: string;
    stemBranch: string;
    theme: string;
    majorStars: string[];
    transformations: Array<{
      star: string;
      code: 'A' | 'B' | 'C' | 'D';
      label: string;
      traditionalName: string;
      palaceKey: string;
      palace: string;
    }>;
    nextYear: {
      year: number;
      nominalAge: number;
      palaceKey: string;
      palace: string;
      stemBranch: string;
      majorStars: string[];
      transformations: Array<{
        star: string;
        code: 'A' | 'B' | 'C' | 'D';
        label: string;
        traditionalName: string;
        palaceKey: string;
        palace: string;
      }>;
    };
    monthlyWindows: Array<{ month: string; palace: string; prompt: string }>;
  };
  timingPreview: string;
};

export type EnglishMvpBirthOptions = {
  birthTime?: string;
  useTrueSolarTime?: boolean;
  longitude?: number;
  timezoneOffsetHours?: number;
};

const PALACE_LABELS: Record<string, string> = {
  soul: 'Life & Direction',
  parents: 'Roots & Guidance',
  spirit: 'Well-being & Inner Drive',
  property: 'Home & Foundations',
  career: 'Career & Public Path',
  friends: 'Network & Collaboration',
  surface: 'Travel & Outer World',
  health: 'Energy & Recovery',
  wealth: 'Wealth & Resources',
  children: 'Creativity & Legacy',
  spouse: 'Partnership & Commitment',
  siblings: 'Peers & Collaboration',
};

const TRANSFORMATION_LABELS = {
  A: { label: 'Flow', traditionalName: '化祿' },
  B: { label: 'Agency', traditionalName: '化权' },
  C: { label: 'Recognition', traditionalName: '化科' },
  D: { label: 'Friction', traditionalName: '化忌' },
} as const;

function titleCase(value: string) {
  return value.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function numericLunarDate(solarDate: string) {
  const [year, month, day] = solarDate.split('-').map(Number);
  const lunar = Solar.fromYmd(year!, month!, day!).getLunar();
  const lunarMonth = lunar.getMonth();
  return {
    value: `${lunar.getYear()}-${String(Math.abs(lunarMonth)).padStart(2, '0')}-${String(lunar.getDay()).padStart(2, '0')}`,
    leapMonth: lunarMonth < 0,
  };
}

export function defaultEnglishTimeForIndex(index: number) {
  if (index <= 0) return '00:30';
  if (index >= 12) return '23:30';
  return `${String(index * 2 - 1).padStart(2, '0')}:30`;
}

const annualPrompts: Record<string, string> = {
  soul: 'name the direction you want your next decisions to reinforce.',
  parents: 'use context, mentorship, and family history as practical support.',
  spirit: 'protect energy, attention, and the habits that make good decisions possible.',
  property: 'strengthen the routines, environment, and resources that support bigger moves.',
  career: 'make useful work visible and define what a credible next contribution looks like.',
  friends: 'choose reciprocal collaborations and make the next ask specific.',
  surface: 'test a new audience, market, or working environment before committing heavily.',
  health: 'treat recovery and capacity as part of the plan, not an afterthought.',
  wealth: 'clarify the return loop: offer, price, buffer, and actual resource use.',
  children: 'turn an idea into a small repeatable output and learn from the response.',
  spouse: 'make expectations, timing, and responsibilities explicit in key commitments.',
  siblings: 'clarify ownership with peers and do not let comparison choose your next move.',
};

function annualPrompt(palaceKey: string) {
  return annualPrompts[palaceKey] ?? 'turn the theme into one observable experiment before making a bigger commitment.';
}

function annualLayer(chart: any, year: number) {
  const horoscope = chart.horoscope(`${year}-07-01 12:00`);
  const annualPalace = chart.palaces.find((palace: any) => palace.index === horoscope.yearly.index) ?? chart.palaces[0];
  const codes = ['A', 'B', 'C', 'D'] as const;
  const transformations = (horoscope.yearly.mutagen as string[]).map((starName, index) => {
    const sourcePalace = chart.palaces.find((palace: any) => [...palace.majorStars, ...palace.minorStars].some((star: any) => star.name === starName)) ?? annualPalace;
    const code = codes[index] ?? 'D';
    return {
      star: titleCase(starName),
      code,
      ...TRANSFORMATION_LABELS[code],
      palaceKey: sourcePalace.name,
      palace: PALACE_LABELS[sourcePalace.name] ?? titleCase(sourcePalace.name),
    };
  });
  return {
    year,
    nominalAge: horoscope.age.nominalAge,
    palaceKey: annualPalace.name,
    palace: PALACE_LABELS[annualPalace.name] ?? titleCase(annualPalace.name),
    stemBranch: `${titleCase(horoscope.yearly.heavenlyStem)} · ${titleCase(horoscope.yearly.earthlyBranch)}`,
    majorStars: annualPalace.majorStars.map((star: any) => titleCase(star.name)),
    transformations,
  };
}

function palaceSnapshot(chart: any, name: string) {
  const palace = chart.palaces.find((item: any) => item.name === name) ?? chart.palaces[0];
  return {
    stars: palace.majorStars.map((star: any) => titleCase(star.name)),
    range: `${palace.decadal.range[0]}–${palace.decadal.range[1]}`,
  };
}

export function buildEnglishMvpSnapshot(
  birthDate: string,
  timeIndex: number,
  gender: EnglishMvpGender,
  asOfYear = new Date().getFullYear(),
  options: EnglishMvpBirthOptions = {},
): EnglishMvpSnapshot {
  const civilTime = options.birthTime || defaultEnglishTimeForIndex(timeIndex);
  validateBirthDateTime(birthDate, civilTime);
  if (!Number.isInteger(asOfYear) || asOfYear < 1900 || asOfYear > 2100) throw new RangeError('Invalid reference year');
  let effectiveBirthDate = birthDate;
  let effectiveBirthTime = civilTime;
  let effectiveTimeIndex = timeIndex;
  let correctionMinutes: number | null = null;

  if (options.useTrueSolarTime) {
    const trueSolar = calculateTrueSolarTime({
      date: birthDate,
      time: civilTime,
      longitude: options.longitude ?? 0,
      timezoneOffsetHours: options.timezoneOffsetHours ?? 0,
    });
    effectiveBirthDate = trueSolar.correctedDate;
    effectiveBirthTime = trueSolar.correctedTime;
    effectiveTimeIndex = trueSolar.timeIndex;
    correctionMinutes = trueSolar.totalCorrectionMinutes;
  } else {
    effectiveTimeIndex = inputTimeToIndex(civilTime);
  }

  const chart = (astro as any).bySolar(effectiveBirthDate, effectiveTimeIndex, gender, true, 'en-US');
  const lunarNumeric = numericLunarDate(effectiveBirthDate);
  // Annual reports consistently use July 1, including the engine's lunar age.
  const annualHoroscope = chart.horoscope(`${asOfYear}-07-01 12:00`);
  const nominalAge = annualHoroscope.age.nominalAge;
  const cycles = [...chart.palaces]
    .map((palace: any) => ({
      palace: PALACE_LABELS[palace.name] ?? titleCase(palace.name),
      start: palace.decadal.range[0],
      end: palace.decadal.range[1],
    }))
    .sort((left: any, right: any) => left.start - right.start);
  const current = cycles.find((cycle: any) => nominalAge >= cycle.start && nominalAge <= cycle.end) ?? cycles[0]!;
  const next = cycles.find((cycle: any) => cycle.start > nominalAge) ?? cycles.at(-1) ?? current;
  const wealth = palaceSnapshot(chart, 'wealth');
  const career = palaceSnapshot(chart, 'career');
  const life = palaceSnapshot(chart, 'soul');
  const palaces = [...chart.palaces]
    .map((palace: any) => ({
      key: palace.name,
      label: PALACE_LABELS[palace.name] ?? titleCase(palace.name),
      stars: palace.majorStars.map((star: any) => titleCase(star.name)),
      supportingStars: [...new Set([...palace.minorStars, ...palace.adjectiveStars].map((star: any) => titleCase(star.name)))],
      range: `${palace.decadal.range[0]}–${palace.decadal.range[1]}`,
      index: palace.index,
      heavenlyStem: titleCase(palace.heavenlyStem),
      earthlyBranch: titleCase(palace.earthlyBranch),
      isBodyPalace: palace.isBodyPalace,
      ages: palace.ages,
      transformations: [...palace.majorStars, ...palace.minorStars]
        .filter((star: any) => star.mutagen && star.mutagen in TRANSFORMATION_LABELS)
        .map((star: any) => {
          const code = star.mutagen as keyof typeof TRANSFORMATION_LABELS;
          return {
            star: titleCase(star.name),
            code,
            ...TRANSFORMATION_LABELS[code],
          };
        }),
    }))
    .sort((left: any, right: any) => left.index - right.index);
  const annual = annualLayer(chart, asOfYear);
  const nextAnnual = annualLayer(chart, asOfYear + 1);
  const monthFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' });
  const monthlyWindows = Array.from({ length: 12 }, (_, index) => {
    const month = index + 1;
    const horoscope = chart.horoscope(`${asOfYear}-${String(month).padStart(2, '0')}-15 12:00`);
    const palace = chart.palaces.find((item: any) => item.index === horoscope.monthly.index) ?? chart.palaces[0];
    return {
      month: monthFormatter.format(new Date(Date.UTC(asOfYear, index, 1))),
      palace: PALACE_LABELS[palace.name] ?? titleCase(palace.name),
      prompt: annualPrompt(palace.name),
    };
  });

  return {
    birthDate,
    effectiveBirthDate,
    effectiveBirthTime,
    timeAccuracy: {
      basis: options.useTrueSolarTime ? 'true_solar' : 'civil',
      timeIndex: effectiveTimeIndex,
      timeWindow: ENGLISH_TIME_SLOTS[effectiveTimeIndex] ?? ENGLISH_TIME_SLOTS[timeIndex]!,
      correctionMinutes,
      chartBoundaryChanged: effectiveBirthDate !== birthDate || effectiveTimeIndex !== timeIndex,
    },
    lunarDate: chart.lunarDate,
    lunarDateNumeric: lunarNumeric.value,
    lunarLeapMonth: lunarNumeric.leapMonth,
    soul: titleCase(chart.soul),
    body: titleCase(chart.body),
    life,
    wealth,
    career,
    palaces,
    currentCycle: { palace: current.palace, range: `${current.start}–${current.end}` },
    nextCycle: { palace: next.palace, range: `${next.start}–${next.end}` },
    annual: {
      ...annual,
      theme: `Your ${asOfYear} planning lens highlights ${annual.palace.toLowerCase()}. Use it to ${annualPrompt(annual.palaceKey)}`,
      nextYear: nextAnnual,
      monthlyWindows,
    },
    timingPreview: `Your current ${current.start}–${current.end} cycle foregrounds ${current.palace.toLowerCase()}. The next visible shift begins at ${next.start}–${next.end}, through ${next.palace.toLowerCase()}.`,
  };
}

export const ENGLISH_TIME_SLOTS = [
  '00:00–00:59', '01:00–02:59', '03:00–04:59', '05:00–06:59', '07:00–08:59',
  '09:00–10:59', '11:00–12:59', '13:00–14:59', '15:00–16:59', '17:00–18:59',
  '19:00–20:59', '21:00–22:59', '23:00–23:59',
];
